
import { useState, useEffect } from 'react';

const COOKIE_CONSENT_KEY = 'mm_cookie_consent';

type ConsentState = 'accepted' | 'declined' | null;

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<ConsentState>(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentState;
    if (!stored) {
      // Small delay so it doesn't flash immediately on load
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
    setConsent(stored);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setConsent('accepted');
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setConsent('declined');
    setVisible(false);

    // Disable GA4 tracking if declined
    if (typeof window !== 'undefined') {
      (window as any)['ga-disable-G-2DDS0SZXHG'] = true;
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
      role="dialog"
      aria-label="Cookie consent"
      aria-modal="false"
    >
      <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Main Banner */}
        <div className="p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            {/* Icon + Text */}
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-red-600 text-xl"></i>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 mb-1">
                  We use cookies &amp; tracking technologies
                </h2>
                <p className="text-xs text-gray-600 leading-relaxed">
                  We use cookies and similar technologies (including Google Analytics and Contentsquare) to understand how visitors use our site, improve your experience, and for analytics purposes. By clicking <strong>"Accept All"</strong>, you consent to our use of these technologies. You may decline non-essential tracking at any time.{' '}
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-red-600 hover:text-red-700 underline cursor-pointer font-medium"
                  >
                    {showDetails ? 'Hide details' : 'Learn more'}
                  </button>
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0 md:mt-0">
              <button
                onClick={handleDecline}
                className="px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-150 cursor-pointer whitespace-nowrap"
              >
                Decline
              </button>
              <button
                onClick={handleAccept}
                className="px-5 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-150 cursor-pointer whitespace-nowrap shadow-sm"
              >
                Accept All
              </button>
            </div>
          </div>

          {/* Expandable Details */}
          {showDetails && (
            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Essential */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-800">Essential Cookies</span>
                  <span className="text-xs text-white bg-gray-500 rounded-full px-2 py-0.5">Always On</span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Required for the site to function — login sessions, security, and basic navigation. Cannot be disabled.
                </p>
              </div>

              {/* Analytics */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-800">Analytics (GA4)</span>
                  <span className="text-xs text-white bg-red-500 rounded-full px-2 py-0.5">Optional</span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Google Analytics 4 tracks page views, sessions, and traffic sources to help us improve the site.
                </p>
              </div>

              {/* Heatmaps */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-800">Heatmaps (Contentsquare)</span>
                  <span className="text-xs text-white bg-red-500 rounded-full px-2 py-0.5">Optional</span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Contentsquare records session replays and heatmaps to help us understand how visitors interact with each page.
                </p>
              </div>

              {/* Privacy links */}
              <div className="md:col-span-3 flex items-center gap-4 pt-1">
                <a
                  href="/privacy-policy"
                  className="text-xs text-red-600 hover:text-red-700 underline cursor-pointer"
                  rel="nofollow"
                >
                  Privacy Policy
                </a>
                <a
                  href="/cookie-policy"
                  className="text-xs text-red-600 hover:text-red-700 underline cursor-pointer"
                  rel="nofollow"
                >
                  Cookie Policy
                </a>
                <span className="text-xs text-gray-400">
                  Compliant with GDPR &amp; CCPA
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
